import React, { useMemo, useRef, useState } from 'react';
import { Search, UserPlus, MessageCircle, X, Trash2, Star, Users, Sparkles, Clock, Database, CalendarClock, LayoutList, Columns3, Gift, Download, Upload, MessagesSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCustomers } from '../hooks/useCustomers';
import { useCustomerMessages } from '../hooks/useCustomerMessages';
import { readXlsxRows } from '../utils/xlsx';
import { customerStage, PROSPECT_STAGES, phoneKey, isLidOnly, phoneLabel, followUpState, isFollowUpDue, addDaysStr, INACTIVE_DAYS, loyaltyProgress, customersToCsv, parseCustomersCsv, parseCustomerCells, localDateStr } from '../utils/customers';

const STAGES = {
  lead: { label: 'Lead', help: 'Escribió pero aún no agenda ni pide', chip: 'bg-sky-500/15 text-sky-300 border-sky-500/30' },
  contactado: { label: 'Contactado', help: 'Ya le escribiste o lo llamaste', chip: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
  interesado: { label: 'Interesado', help: 'Mostró interés', chip: 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30' },
  negociacion: { label: 'En negociación', help: 'Está decidiendo o cotizando', chip: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30' },
  cliente: { label: 'Cliente', help: 'Ya tiene citas o pedidos', chip: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  vip: { label: 'VIP', help: 'Marcado manualmente', chip: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  perdido: { label: 'Perdido', help: 'No le interesa: no se le incluye en campañas', chip: 'bg-rose-500/15 text-rose-300 border-rose-500/30' },
  inactivo: { label: 'Inactivo', help: `Sin actividad hace más de ${INACTIVE_DAYS} días, o marcado a mano`, chip: 'bg-slate-500/15 text-slate-300 border-slate-500/30' }
};

// Marcas que una persona puede poner en la ficha (la automática deja que el sistema calcule lead/cliente/inactivo).
const MARK_OPTIONS = [
  ['auto', 'Automática (lead / cliente)'],
  ['contactado', 'Contactado'],
  ['interesado', 'Interesado'],
  ['negociacion', 'En negociación'],
  ['vip', 'VIP'],
  ['perdido', 'Perdido (no le interesa)'],
  ['inactivo', 'Inactivo']
];

const money = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const shortDate = (iso) => (iso ? String(iso).split('T')[0] : '—');
const inputCls = 'w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green';

function StageChip({ stage }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold ${STAGES[stage].chip}`}>
      {STAGES[stage].label}
    </span>
  );
}

const FOLLOW_UP_BADGE = {
  vencido: { text: 'Seguimiento vencido', cls: 'bg-red-500/15 text-red-300 border-red-500/30' },
  hoy: { text: 'Contactar hoy', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
  proximo: { text: 'Seguimiento', cls: 'bg-slate-500/15 text-slate-300 border-slate-500/30' }
};

function FollowUpBadge({ customer }) {
  const state = followUpState(customer);
  if (!state) return null;
  const { text, cls } = FOLLOW_UP_BADGE[state];
  const day = String(customer.next_follow_up_at).slice(0, 10).split('-').reverse().slice(0, 2).join('/');
  return (
    <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full border text-[10px] font-bold ${cls}`}>
      <CalendarClock size={10} /> {text}{state === 'proximo' ? ` · ${day}` : ''}
    </span>
  );
}

function FollowUpSection({ customer, onSave, readOnly }) {
  const set = (date) => onSave(customer.id, { next_follow_up_at: date });
  return (
    <div className="space-y-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3">
      <label className="block text-[10px] uppercase font-bold text-seryi-text">Próximo seguimiento
        <input
          id="customer-followup" type="date" disabled={readOnly} className={`${inputCls} mt-1`}
          value={String(customer.next_follow_up_at || '').slice(0, 10)}
          onChange={(e) => set(e.target.value || null)}
        />
      </label>
      <div className="flex flex-wrap gap-1.5">
        {[['Mañana', 1], ['En 3 días', 3], ['En 1 semana', 7]].map(([label, days]) => (
          <button
            key={label} type="button" disabled={readOnly} onClick={() => set(addDaysStr(days))}
            className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-seryi-bg text-seryi-text hover:text-seryi-heading border border-seryi-bg-light disabled:opacity-50"
          >
            {label}
          </button>
        ))}
        {customer.next_follow_up_at && (
          <button
            type="button" disabled={readOnly} onClick={() => set(null)}
            className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-seryi-green/15 text-seryi-green border border-seryi-green/30 disabled:opacity-50"
          >
            ✓ Ya lo contacté
          </button>
        )}
      </div>
    </div>
  );
}

function LoyaltySection({ customer, completedVisits, loyalty, onSave, readOnly }) {
  const p = loyaltyProgress(completedVisits, customer.loyalty_redeemed, loyalty.visitsRequired);
  const circles = p.required <= 12 ? Array.from({ length: p.required }, (_, i) => i < p.stamps) : null;
  return (
    <div className="space-y-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3">
      <div className="flex items-center justify-between">
        <h4 className="text-[10px] uppercase font-bold tracking-wider text-seryi-text flex items-center gap-1.5"><Gift size={12} /> Tarjeta de sellos</h4>
        <span className="text-[11px] font-bold text-seryi-heading">{p.stamps} de {p.required}</span>
      </div>
      {circles && (
        <div className="flex flex-wrap gap-1.5" aria-label="Sellos">
          {circles.map((on, i) => (
            <span key={i} className={`w-5 h-5 rounded-full border ${on ? 'bg-seryi-green border-seryi-green' : 'border-seryi-bg-light'}`} />
          ))}
        </div>
      )}
      <p className="text-[11px] text-seryi-text">
        Recompensa: <b className="text-seryi-heading">{loyalty.rewardLabel}</b> · ganadas {p.earned} · canjeadas {p.redeemed}
      </p>
      <button
        type="button" disabled={readOnly || p.available === 0}
        onClick={() => onSave(customer.id, { loyalty_redeemed: p.redeemed + 1 })}
        className="w-full bg-seryi-green text-seryi-bg-dark font-extrabold text-xs py-2 rounded-xl hover:brightness-110 disabled:opacity-40"
      >
        {p.available > 0 ? `Canjear recompensa (${p.available} disponible${p.available === 1 ? '' : 's'})` : 'Sin recompensas disponibles'}
      </button>
    </div>
  );
}

// Tablero por etapa. Arrastrar a VIP o Inactivo marca a la persona; soltar en Lead o Cliente quita la marca
// (esas dos etapas se calculan solas según su actividad).
const BOARD_COLUMNS = [
  { key: 'lead', status: 'auto' },
  { key: 'contactado', status: 'contactado' },
  { key: 'interesado', status: 'interesado' },
  { key: 'negociacion', status: 'negociacion' },
  { key: 'cliente', status: 'auto' },
  { key: 'vip', status: 'vip' },
  { key: 'perdido', status: 'perdido' },
  { key: 'inactivo', status: 'inactivo' }
];

function CustomerBoard({ rows, onOpen, onMove, readOnly }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {BOARD_COLUMNS.map(({ key, status }) => {
        const cards = rows.filter(r => r.stage === key);
        return (
          <div
            key={key} data-testid={`board-${key}`}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const id = e.dataTransfer.getData('text/plain');
              const row = rows.find(r => r.customer.id === id);
              if (row && row.stage !== key) onMove(row, key, status);
            }}
            className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-3 space-y-2 min-h-[8rem] min-w-[12.5rem] flex-1"
          >
            <div className="flex items-center justify-between">
              <StageChip stage={key} />
              <span className="text-[11px] font-bold text-seryi-text">{cards.length}</span>
            </div>
            {cards.map(r => (
              <div
                key={r.customer.id} role="button" tabIndex={0} draggable={!readOnly}
                onDragStart={(e) => e.dataTransfer.setData('text/plain', r.customer.id)}
                onClick={() => onOpen(r.customer.id)}
                onKeyDown={(e) => e.key === 'Enter' && onOpen(r.customer.id)}
                className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5 cursor-pointer hover:border-seryi-green/40"
              >
                <p className="text-xs font-bold text-seryi-heading truncate">{r.customer.name || 'Sin nombre'}</p>
                <p className="text-[10px] text-seryi-text font-mono">{phoneLabel(r.customer)}</p>
                <FollowUpBadge customer={r.customer} />
              </div>
            ))}
            {cards.length === 0 && <p className="text-[11px] text-seryi-text/70 italic">Nadie por aquí</p>}
          </div>
        );
      })}
    </div>
  );
}

const formatMessageTime = (iso) => new Date(iso).toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

function ConversationSection({ customer }) {
  const { messages, loading, tableMissing } = useCustomerMessages(customer);
  const scrollRef = useRef(null);
  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  return (
    <div className="space-y-2">
      <h4 className="text-[10px] uppercase font-bold tracking-wider text-seryi-text flex items-center gap-1.5">
        <MessagesSquare size={12} /> Conversación de WhatsApp {messages.length > 0 && `(${messages.length})`}
      </h4>
      {tableMissing ? (
        <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
          Falta ejecutar <code className="font-mono">20260930e_customer_messages.sql</code> en Supabase para guardar las conversaciones.
        </p>
      ) : loading ? (
        <p className="text-xs text-seryi-text italic">Cargando conversación…</p>
      ) : messages.length === 0 ? (
        <p className="text-xs text-seryi-text italic">Todavía no hay mensajes guardados. Desde que se activó el guardado, cada mensaje con esta persona aparece aquí.</p>
      ) : (
        <div ref={scrollRef} data-testid="conversation" className="max-h-72 overflow-y-auto space-y-1.5 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-2.5">
          {messages.map(m => (
            <div key={m.id} className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-xs ${m.direction === 'out' ? 'bg-seryi-green/20 text-seryi-heading rounded-br-sm' : 'bg-seryi-bg text-seryi-heading rounded-bl-sm border border-seryi-bg-light'}`}>
                <p className="whitespace-pre-wrap break-words">{m.body}</p>
                <p className="text-[9px] text-seryi-text mt-0.5 text-right">{formatMessageTime(m.sent_at)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LinkPhoneForm({ onLink, readOnly }) {
  const [phone, setPhone] = useState('');
  const submit = (e) => {
    e.preventDefault();
    onLink(phone);
  };
  return (
    <form onSubmit={submit} className="space-y-2 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3">
      <p className="text-xs text-seryi-text">
        WhatsApp no compartió el número de esta persona, solo un identificador interno. Si lo conoces, vincúlalo para ver su historial y escribirle.
      </p>
      <label className="block text-[10px] uppercase font-bold text-seryi-text">Teléfono (con indicativo)
        <input id="link-phone" className={`${inputCls} mt-1`} placeholder="573001112233" value={phone} onChange={(e) => setPhone(e.target.value)} />
      </label>
      <button type="submit" disabled={readOnly || !phone.trim()} className="w-full bg-seryi-green text-seryi-bg-dark font-extrabold text-xs py-2 rounded-xl hover:brightness-110 disabled:opacity-50">
        Vincular teléfono
      </button>
    </form>
  );
}

function CustomerDetail({ row, stage, onClose, onSave, onDelete, onLink, loyalty, readOnly, fiscal = false, priceLists = [] }) {
  const { customer, history, visits, completedVisits, orderCount, totalSpent } = row;
  const [form, setForm] = useState({
    name: customer.name || '', email: customer.email || '', notes: customer.notes || '',
    tags: (customer.tags || []).join(', '), status: customer.status || 'auto',
    price_list_key: customer.price_list_key || '', tax_id: customer.tax_id || '', legal_name: customer.legal_name || '',
    billing_email: customer.billing_email || '', billing_address: customer.billing_address || '', billing_city: customer.billing_city || ''
  });

  // Los campos fiscales y la lista de precios solo se envían si el módulo está encendido (así la ficha sigue funcionando sin esas migraciones).
  const clean = (v) => v.trim() || null;
  const save = () => onSave(customer.id, {
    name: form.name.trim() || null,
    email: form.email.trim() || null,
    notes: form.notes.trim() || null,
    tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
    status: form.status,
    ...(priceLists.length > 0 ? { price_list_key: form.price_list_key || null } : {}),
    ...(fiscal ? {
      tax_id: clean(form.tax_id), legal_name: clean(form.legal_name), billing_email: clean(form.billing_email),
      billing_address: clean(form.billing_address), billing_city: clean(form.billing_city)
    } : {})
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60" onClick={onClose}>
      <aside
        className="w-full max-w-md h-full overflow-y-auto bg-seryi-bg border-l border-seryi-bg-light p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-extrabold text-lg text-seryi-heading truncate">{customer.name || 'Sin nombre'}</h3>
            <p className="text-xs text-seryi-text font-mono">{phoneLabel(customer)}</p>
            <div className="mt-2"><StageChip stage={stage} /></div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="p-2 rounded-xl hover:bg-seryi-bg-light/60 text-seryi-text"><X size={18} /></button>
        </div>

        {isLidOnly(customer) ? (
          <LinkPhoneForm onLink={(phone) => onLink(customer.id, phone)} readOnly={readOnly} />
        ) : (
          <a
            href={`https://wa.me/${customer.phone}`} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-2 bg-seryi-green text-seryi-bg-dark font-extrabold text-xs py-2.5 rounded-xl hover:brightness-110"
          >
            <MessageCircle size={15} /> Escribir por WhatsApp
          </a>
        )}

        <FollowUpSection customer={customer} onSave={onSave} readOnly={readOnly} />

        {loyalty && <LoyaltySection customer={customer} completedVisits={completedVisits || 0} loyalty={loyalty} onSave={onSave} readOnly={readOnly} />}

        <div className="grid grid-cols-3 gap-2 text-center">
          {[['Citas', visits], ['Pedidos', orderCount], ['Gastado', money(totalSpent)]].map(([k, v]) => (
            <div key={k} className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl p-3">
              <p className="text-sm font-black text-seryi-heading">{v}</p>
              <p className="text-[10px] uppercase tracking-wider text-seryi-text">{k}</p>
            </div>
          ))}
        </div>

        <div className="text-[11px] text-seryi-text grid grid-cols-2 gap-2">
          <span>Primer contacto: <b className="text-seryi-heading">{shortDate(customer.first_contact_at)}</b></span>
          <span>Último contacto: <b className="text-seryi-heading">{shortDate(customer.last_contact_at)}</b></span>
          <span>Mensajes: <b className="text-seryi-heading">{customer.messages_count || 0}</b></span>
          <span>Origen: <b className="text-seryi-heading">{customer.source}</b></span>
        </div>

        <div className="space-y-3">
          <label className="block text-[10px] uppercase font-bold text-seryi-text">Nombre
            <input id="customer-name" className={`${inputCls} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="block text-[10px] uppercase font-bold text-seryi-text">Correo
            <input id="customer-email" type="email" className={`${inputCls} mt-1`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </label>
          <label className="block text-[10px] uppercase font-bold text-seryi-text">Etiquetas (separadas por coma)
            <input id="customer-tags" className={`${inputCls} mt-1`} placeholder="alérgico, prefiere sábados" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          </label>
          <label className="block text-[10px] uppercase font-bold text-seryi-text">Notas
            <textarea id="customer-notes" rows={3} className={`${inputCls} mt-1`} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </label>
          <label className="block text-[10px] uppercase font-bold text-seryi-text">Marca
            <select id="customer-status" className={`${inputCls} mt-1`} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {MARK_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <p className="text-[10px] text-seryi-text -mt-1">
            Contactado, Interesado y En negociación aplican mientras sea lead: apenas agenda o pide, pasa solo a Cliente. Perdido lo excluye de las campañas.
          </p>
          {priceLists.length > 0 && (
            <label className="block text-[10px] uppercase font-bold text-seryi-text">Lista de precios
              <select id="customer-price-list" className={`${inputCls} mt-1`} value={form.price_list_key} onChange={(e) => setForm({ ...form, price_list_key: e.target.value })}>
                <option value="">Predeterminada del negocio</option>
                {priceLists.map((l) => <option key={l.key} value={l.key}>{l.name}</option>)}
              </select>
              <span className="block normal-case font-normal text-[10px] mt-1">Es la que usan el bot y las cotizaciones para este cliente.</span>
            </label>
          )}
          {fiscal && (
            <fieldset className="space-y-3 border border-seryi-bg-light rounded-xl p-3">
              <legend className="px-1 text-[10px] uppercase font-bold text-seryi-text">Datos fiscales</legend>
              <label className="block text-[10px] uppercase font-bold text-seryi-text">NIT o cédula
                <input id="customer-tax-id" className={`${inputCls} mt-1`} value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} />
              </label>
              <label className="block text-[10px] uppercase font-bold text-seryi-text">Razón social
                <input id="customer-legal-name" className={`${inputCls} mt-1`} value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} />
              </label>
              <label className="block text-[10px] uppercase font-bold text-seryi-text">Correo de facturación
                <input id="customer-billing-email" type="email" className={`${inputCls} mt-1`} value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })} />
              </label>
              <label className="block text-[10px] uppercase font-bold text-seryi-text">Dirección
                <input id="customer-billing-address" className={`${inputCls} mt-1`} value={form.billing_address} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
              </label>
              <label className="block text-[10px] uppercase font-bold text-seryi-text">Ciudad
                <input id="customer-billing-city" className={`${inputCls} mt-1`} value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} />
              </label>
              <p className="text-[10px] text-seryi-text">Salen en el encabezado de las cotizaciones. Datos personales: se tratan según la Ley 1581 de 2012; el cliente puede pedir corregirlos o borrarlos.</p>
            </fieldset>
          )}
        </div>

        <div className="flex gap-2">
          <button onClick={save} disabled={readOnly} className="flex-1 bg-seryi-green text-seryi-bg-dark font-extrabold text-xs py-2.5 rounded-xl hover:brightness-110 disabled:opacity-50">
            Guardar ficha
          </button>
          <button onClick={() => onDelete(customer.id).then(ok => ok && onClose())} disabled={readOnly} aria-label="Eliminar cliente" className="px-3 rounded-xl border border-red-500/40 text-red-400 hover:bg-red-500/10 disabled:opacity-50">
            <Trash2 size={15} />
          </button>
        </div>

        <ConversationSection customer={customer} />

        <div>
          <h4 className="text-[10px] uppercase font-bold tracking-wider text-seryi-text mb-2">Historial</h4>
          {history.length === 0 ? (
            <p className="text-xs text-seryi-text italic">Todavía no ha agendado ni pedido nada.</p>
          ) : (
            <ul className="space-y-1.5">
              {history.map((h, i) => (
                <li key={`${h.type}-${h.date}-${i}`} className="flex items-center justify-between gap-3 bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <p className="font-bold text-seryi-heading truncate">{h.type === 'Cita' ? '🗓️' : '🛍️'} {h.label}</p>
                    <p className="text-[10px] text-seryi-text">{shortDate(h.date)} · {h.status}</p>
                  </div>
                  <span className={`font-bold shrink-0 ${h.paid ? 'text-emerald-300' : 'text-seryi-text'}`}>{money(h.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function NewCustomerForm({ onSubmit, onCancel }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const submit = async (e) => {
    e.preventDefault();
    if (await onSubmit(form)) onCancel();
  };
  return (
    <form onSubmit={submit} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
      <label className="text-[10px] uppercase font-bold text-seryi-text">Nombre
        <input id="new-customer-name" className={`${inputCls} mt-1`} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label className="text-[10px] uppercase font-bold text-seryi-text">WhatsApp (con indicativo)
        <input id="new-customer-phone" required className={`${inputCls} mt-1`} placeholder="573001112233" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      </label>
      <label className="text-[10px] uppercase font-bold text-seryi-text">Correo
        <input id="new-customer-email" type="email" className={`${inputCls} mt-1`} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </label>
      <div className="flex gap-2">
        <button type="submit" className="flex-1 bg-seryi-green text-seryi-bg-dark font-extrabold text-xs py-2.5 rounded-xl hover:brightness-110">Guardar</button>
        <button type="button" onClick={onCancel} className="px-4 text-xs font-bold text-seryi-text rounded-xl border border-seryi-bg-light hover:text-seryi-heading">Cancelar</button>
      </div>
    </form>
  );
}

export default function CustomersPage() {
  const { customers, statsByKey, loading, tableMissing, isReadOnlyDemo, addCustomer, updateCustomer, linkPhone, importCustomers, deleteCustomer, loyalty, fiscal, priceLists } = useCustomers();
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [listFilter, setListFilter] = useState('all'); // 'all' | 'none' (sin lista) | clave de una lista de precios
  const [showNew, setShowNew] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'board'
  const fileInputRef = useRef(null);

  const rows = useMemo(() => customers.map(c => {
    const stats = statsByKey.get(phoneKey(c.phone));
    return { ...stats, customer: c, stage: customerStage(c, stats) };
  }), [customers, statsByKey]);

  const counts = useMemo(() => ({
    all: rows.length,
    lead: rows.filter(r => r.stage === 'lead').length,
    contactado: rows.filter(r => r.stage === 'contactado').length,
    interesado: rows.filter(r => r.stage === 'interesado').length,
    negociacion: rows.filter(r => r.stage === 'negociacion').length,
    prospects: rows.filter(r => PROSPECT_STAGES.includes(r.stage)).length,
    perdido: rows.filter(r => r.stage === 'perdido').length,
    cliente: rows.filter(r => r.stage === 'cliente').length,
    vip: rows.filter(r => r.stage === 'vip').length,
    inactivo: rows.filter(r => r.stage === 'inactivo').length,
    followup: rows.filter(r => isFollowUpDue(r.customer)).length
  }), [rows]);

  // Lista de precios del cliente (empresa, distribuidor…): para verla en la tabla y filtrar. «Sin lista» = ve la predeterminada del negocio.
  const hasLists = Array.isArray(priceLists) && priceLists.length > 0;
  const listName = (key) => (key ? (priceLists || []).find(l => l.key === key)?.name || key : '');
  const matchesList = (r) => !hasLists || listFilter === 'all' || (listFilter === 'none' ? !r.customer.price_list_key : r.customer.price_list_key === listFilter);
  const listCounts = useMemo(() => {
    const m = { none: 0 };
    rows.forEach(r => { const k = r.customer.price_list_key; if (k) m[k] = (m[k] || 0) + 1; else m.none += 1; });
    return m;
  }, [rows]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter(r => {
      if (!matchesList(r)) return false;
      if (stageFilter === 'followup') {
        if (!isFollowUpDue(r.customer)) return false;
      } else if (stageFilter !== 'all' && r.stage !== stageFilter) return false;
      if (!q) return true;
      const c = r.customer;
      return (c.name || '').toLowerCase().includes(q) || c.phone.includes(q.replace(/\D/g, '') || q) ||
        (c.tags || []).some(t => t.toLowerCase().includes(q));
    });
    // En "Por contactar" van primero los más atrasados.
    return stageFilter === 'followup'
      ? [...list].sort((a, b) => String(a.customer.next_follow_up_at).localeCompare(String(b.customer.next_follow_up_at)))
      : list;
  }, [rows, search, stageFilter, listFilter, priceLists]);

  const selected = rows.find(r => r.customer.id === selectedId);

  // El tablero ignora el filtro de etapa (las columnas ya son las etapas) pero respeta la búsqueda.
  const boardRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byList = rows.filter(matchesList);
    return q ? byList.filter(r => (r.customer.name || '').toLowerCase().includes(q) || r.customer.phone.includes(q.replace(/\D/g, '') || q)) : byList;
  }, [rows, search, listFilter, priceLists]);

  // Exporta toda la base (no solo lo filtrado). Separador ";" y BOM: Excel en español lo abre bien.
  const exportCsv = () => {
    const blob = new Blob([customersToCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `clientes-${localDateStr()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const importCsv = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const finish = async (result) => {
      const { rows: parsed, skipped, unknownLists } = result;
      if (parsed.length === 0) {
        toast.error('No encontré teléfonos válidos. Usa columnas Nombre, Teléfono, Correo y Lista de precios (teléfono de mínimo 7 dígitos).');
        return;
      }
      if (unknownLists.length > 0) {
        toast(`Estas listas no existen y esos clientes quedaron sin lista: ${unknownLists.slice(0, 5).join(', ')}${unknownLists.length > 5 ? '…' : ''}. Revisa los nombres en Catálogo → Listas.`, { icon: '⚠️', duration: 9000 });
      }
      const added = await importCustomers(parsed);
      if (added > 0) toast.success(`${added} cliente${added === 1 ? '' : 's'} importado${added === 1 ? '' : 's'}${skipped ? ` · ${skipped} fila${skipped === 1 ? '' : 's'} sin teléfono válido` : ''}`);
    };
    const reader = new FileReader();
    if (/\.xlsx$/i.test(file.name)) {
      reader.onload = async () => {
        try {
          const cells = readXlsxRows(reader.result).map((r) => r.map((c) => (c === null || c === undefined ? '' : String(c))));
          await finish(parseCustomerCells(cells, { lists: priceLists }));
        } catch (e) {
          toast.error('No pude leer ese archivo de Excel. Guárdalo como .xlsx o como CSV e inténtalo de nuevo.');
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      reader.onload = () => finish(parseCustomersCsv(String(reader.result || ''), { lists: priceLists }));
      reader.readAsText(file);
    }
  };

  const moveCustomer = (row, stage, status) => {
    // Las etapas de seguimiento son para leads: una persona que ya agendó o pidió es cliente.
    if (['contactado', 'interesado', 'negociacion'].includes(status) && row.stage === 'cliente') {
      toast('Esta persona ya es cliente (tiene citas o pedidos): las etapas de seguimiento son solo para leads.');
      return;
    }
    updateCustomer(row.customer.id, { status });
    if (status === 'auto') toast('Lead y Cliente se calculan solos según su actividad; se quitó la marca manual.');
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-seryi-bg-light/80 pb-4">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading">Clientes</h2>
          <p className="text-xs text-seryi-text">Quién es cada persona que te escribe, agenda o compra. Todo el que te escribe por WhatsApp queda guardado aquí.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            onClick={exportCsv} disabled={tableMissing || rows.length === 0}
            className="px-3.5 py-2.5 bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <Download size={15} /> Exportar CSV
          </button>
          <button
            onClick={() => fileInputRef.current?.click()} disabled={tableMissing || isReadOnlyDemo}
            className="px-3.5 py-2.5 bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading border border-seryi-bg-light rounded-2xl text-xs font-bold flex items-center gap-2 disabled:opacity-50"
          >
            <Upload size={15} /> Importar clientes
          </button>
          <input ref={fileInputRef} data-testid="csv-input" type="file" accept=".csv,text/csv,text/plain,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={importCsv} />
          <button
            onClick={() => setShowNew(v => !v)} disabled={tableMissing}
            className="px-4 py-2.5 bg-seryi-green text-seryi-bg-dark hover:brightness-110 rounded-2xl text-xs font-extrabold transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
          >
            <UserPlus size={16} /> Nuevo cliente
          </button>
        </div>
      </div>

      {tableMissing && (
        <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-2xl p-4 text-xs">
          <Database size={18} className="shrink-0 mt-0.5" />
          <p>Falta crear la tabla de clientes en Supabase. Ejecuta el archivo <code className="font-mono">supabase/migrations/20260929_create_customers.sql</code> en el SQL Editor y recarga esta página.</p>
        </div>
      )}

      {showNew && !tableMissing && <NewCustomerForm onSubmit={addCustomer} onCancel={() => setShowNew(false)} />}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['Total', counts.all, Users, 'text-seryi-heading'],
          ['Leads', counts.prospects, Sparkles, 'text-sky-300'],
          ['Clientes', counts.cliente, Star, 'text-emerald-300'],
          ['VIP', counts.vip, Clock, 'text-amber-300'],
          ['Por contactar', counts.followup, CalendarClock, 'text-rose-300']
        ].map(([label, value, Icon, color]) => (
          <div key={label} className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4">
            <div className="flex items-center justify-between text-seryi-text">
              <span className="text-[10px] uppercase font-bold tracking-wider">{label}</span>
              <Icon size={15} className={color} />
            </div>
            <p className={`text-2xl font-black mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col md:flex-row gap-3 md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {[['all', 'Todos'], ['lead', 'Leads'], ['contactado', 'Contactados'], ['interesado', 'Interesados'], ['negociacion', 'En negociación'], ['cliente', 'Clientes'], ['vip', 'VIP'], ['perdido', 'Perdidos'], ['inactivo', 'Inactivos'], ['followup', 'Por contactar']].map(([key, label]) => (
            <button
              key={key} onClick={() => setStageFilter(key)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${stageFilter === key ? 'bg-seryi-green text-seryi-bg-dark' : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'}`}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {[['list', 'Lista', LayoutList], ['board', 'Tablero', Columns3]].map(([key, label, Icon]) => (
            <button
              key={key} onClick={() => setView(key)} aria-pressed={view === key}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${view === key ? 'bg-seryi-bg-light text-seryi-heading' : 'bg-seryi-bg-dark text-seryi-text hover:text-seryi-heading'}`}
            >
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>
        {hasLists && (
          <label className="flex items-center gap-2 text-[10px] uppercase font-bold text-seryi-text">
            Lista
            <select
              id="customer-list-filter" aria-label="Filtrar por lista de precios" value={listFilter} onChange={(e) => setListFilter(e.target.value)}
              className="bg-seryi-bg-dark border border-seryi-bg-light rounded-xl px-3 py-2 text-xs normal-case font-normal text-seryi-heading focus:outline-none focus:border-seryi-green"
            >
              <option value="all">Todas ({rows.length})</option>
              {priceLists.map(l => <option key={l.key} value={l.key}>{l.name} ({listCounts[l.key] || 0})</option>)}
              <option value="none">Sin lista ({listCounts.none})</option>
            </select>
          </label>
        )}
        <div className="relative w-full md:w-72">
          <Search className="absolute left-3.5 top-2.5 text-seryi-text" size={15} />
          <input
            id="customer-search" type="text" placeholder="Buscar nombre, teléfono o etiqueta..."
            className="w-full bg-seryi-bg-dark border border-seryi-bg-light rounded-xl pl-9 pr-4 py-2 text-xs text-seryi-heading focus:outline-none focus:border-seryi-green"
            value={search} onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p className="text-xs text-seryi-text py-10 text-center">Cargando clientes…</p>
      ) : view === 'board' ? (
        <CustomerBoard rows={boardRows} onOpen={setSelectedId} onMove={moveCustomer} readOnly={isReadOnlyDemo} />
      ) : visible.length === 0 ? (
        <div className="text-center py-14 bg-seryi-bg border border-seryi-bg-light rounded-3xl">
          <Users size={28} className="mx-auto text-seryi-text mb-3" />
          <p className="text-sm font-bold text-seryi-heading">{rows.length === 0 ? 'Aún no hay clientes guardados' : 'Nada coincide con tu búsqueda'}</p>
          {rows.length === 0 && !tableMissing && (
            <p className="text-xs text-seryi-text mt-1 max-w-sm mx-auto">Cuando alguien te escriba por WhatsApp aparecerá aquí automáticamente, o agrégalo a mano con “Nuevo cliente”.</p>
          )}
        </div>
      ) : (
        <div className="bg-seryi-bg border border-seryi-bg-light rounded-3xl overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[640px]">
            <thead>
              <tr className="border-b border-seryi-bg-light text-[10px] uppercase font-bold text-seryi-text tracking-wider">
                <th className="py-3 px-4">Cliente</th>
                <th className="py-3 px-4">Etapa</th>
                {hasLists && <th className="py-3 px-4">Lista</th>}
                <th className="py-3 px-4 text-right">Citas / Pedidos</th>
                <th className="py-3 px-4 text-right">Gastado</th>
                <th className="py-3 px-4">Último contacto</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-seryi-bg-light/50 text-xs">
              {visible.map(r => (
                <tr
                  key={r.customer.id} onClick={() => setSelectedId(r.customer.id)} tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setSelectedId(r.customer.id)}
                  className="cursor-pointer hover:bg-seryi-bg-dark/40 focus:outline-none focus:bg-seryi-bg-dark/60 transition-colors"
                >
                  <td className="py-3.5 px-4">
                    <p className="font-bold text-seryi-heading">{r.customer.name || 'Sin nombre'}</p>
                    <p className="text-[11px] text-seryi-text font-mono">{phoneLabel(r.customer)}</p>
                    <FollowUpBadge customer={r.customer} />
                  </td>
                  <td className="py-3.5 px-4"><StageChip stage={r.stage} /></td>
                  {hasLists && (
                    <td className="py-3.5 px-4">
                      {r.customer.price_list_key
                        ? <span className="inline-block rounded-full border border-seryi-green/30 bg-seryi-green/10 px-2.5 py-0.5 text-[11px] font-bold text-seryi-green">{listName(r.customer.price_list_key)}</span>
                        : <span className="text-[11px] text-seryi-text">—</span>}
                    </td>
                  )}
                  <td className="py-3.5 px-4 text-right text-seryi-heading">{r.visits} / {r.orderCount}</td>
                  <td className="py-3.5 px-4 text-right font-bold text-seryi-heading">{money(r.totalSpent)}</td>
                  <td className="py-3.5 px-4 text-seryi-text">{shortDate(r.lastActivity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <CustomerDetail
          key={selected.customer.id} row={selected} stage={selected.stage} readOnly={isReadOnlyDemo}
          onClose={() => setSelectedId(null)} onSave={updateCustomer} onDelete={deleteCustomer} onLink={linkPhone} loyalty={loyalty} fiscal={fiscal} priceLists={priceLists}
        />
      )}
    </div>
  );
}
